import loadable from '@loadable/component';
import { type FunctionComponent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MEDIUM_MAX_PX, SMALL_MAX_PX } from '../../constants/breakpoints';
import { MediaGalleryContextProvider } from '../../contexts/pdp/media-gallery.context';
import { type CloudinaryOptions, generateCloudinaryUrl } from '../../helpers/cloudinary-helper/cloudinary-helper';
import { type Media, isThreeSixtyImage, isVideo, type Variation } from '../../helpers/images/images.helper';
import { ImageGalleryCarousel } from '../carousel-components/image-gallery-carousel/image-gallery-carousel.component';
import { imageContainer, imageGalleryWidth, mediaCardHeight, videoCardMaxHeight } from './media-gallery.css';

const LoadableMediaGalleryModal = loadable(
	() => import(/* webpackChunkName: "media-gallery-modal" */ './media-gallery-modal/media-gallery-modal.component'),
	{
		resolveComponent: ({ MediaGalleryModal }) => MediaGalleryModal
	}
);

export type MediaGalleryProps = {
	items: Media[];
	variantImageId?: string;
	title?: string;
	currentVariantImage?: Media;
};

export const MediaGallery: FunctionComponent<MediaGalleryProps> = ({ items, variantImageId, title = '', currentVariantImage }) => {
	const [showModal, setShowModal] = useState(false);
	const initItem = items.length > 0 ? items[0] : null;
	const [currentMedia, setCurrentMedia] = useState(initItem);

	const defaultOptions: CloudinaryOptions = { width: 450, height: 450 };

	const variations: Variation[] = [
		{ mediaCondition: { maxWidth: SMALL_MAX_PX }, options: { width: 330, height: 330 } },
		{ mediaCondition: { maxWidth: MEDIUM_MAX_PX }, options: { width: 450, height: 450, dpr: 2 } }
	];

	// encode the image ID to match the encoding done by srcSet generation,
	// this ensures that the preloaded image url matches the url generated for the main image
	const selectedVariantImageUrl = generateCloudinaryUrl(encodeURIComponent(currentVariantImage?.id || ''), {
		...defaultOptions,
		...variations,
		crop: 'pad',
		dpr: 2
	});

	useEffect(() => {
		if (currentVariantImage && items.length > 0) {
			// will overwrite main image if a variant is clicked on
			setCurrentMedia(currentVariantImage);
		}
	}, [variantImageId]);

	// for when user hovers over variant selector
	const variantHeroOverride = currentVariantImage?.id !== variantImageId ? currentVariantImage : null;

	return (
		<>
			<Helmet>
				<link rel="preload" as="image" href={selectedVariantImageUrl} fetchPriority="high" />
			</Helmet>
			<MediaGalleryContextProvider>
				<div className={`chromatic-ignore bg-theme-white relative mt2-l`}>
					<div className={`${imageGalleryWidth} ${imageContainer}`} role="region" aria-label="main image">
						{currentMedia && (
							<ImageGalleryCarousel
								items={items}
								thumbnailMode="show"
								selectedItemId={currentMedia.id}
								onSelectedImageChange={setCurrentMedia}
								onClick={() => setShowModal(!isVideo(currentMedia) && !isThreeSixtyImage(currentMedia))}
								galleryImageOptions={{ options: defaultOptions, variations, bypassLazyLoad: true }}
								shouldOpenVideoInModal={true}
								videoStyling={videoCardMaxHeight}
								overrideHero={variantHeroOverride}
								cardHeight={mediaCardHeight}
							/>
						)}
					</div>
					{showModal && currentMedia && (
						<LoadableMediaGalleryModal
							items={items}
							onClose={() => setShowModal(false)}
							title={title}
							currentMediaImage={currentMedia}
							onSelectedMediaChange={setCurrentMedia}
							shouldOpenVideoInModal={false}
						/>
					)}
				</div>
			</MediaGalleryContextProvider>
		</>
	);
};
